/**
 * My Support, in 18rem — the sidebar on the conversation page.
 *
 * Deliberately the same four groups in the same order as `/support`, collapsing
 * the same way and remembering their own state: someone who learns the shape
 * here already knows the full page, and vice versa. The only differences are
 * width and depth — no filters, and each group is capped at a handful of rows.
 *
 * "View all" goes to `/support`, and it is the only route there when the rail
 * is showing, which is why it is a real link rather than decoration in the
 * heading. The header drops its own My Support entry whenever this is on
 * screen, so the destination is offered exactly once.
 *
 * Storage keys are prefixed `rail.` so folding a group shut here does not fold
 * the same group on the full page, where the employee has gone specifically to
 * read it.
 */

import { ChevronRight, CircleCheck, Icon, Ticket } from "@norican/icons";
import { Card, cn, Typography } from "@norican/react";
import { Link } from "react-router";

import {
  ErrorState,
  LoadingRows,
  RelativeTime,
  RequesterUnresolvedNotice,
} from "@/components/common";
import { TranslatedText, useConversations } from "@/features/assistant";
import { StarterActionLink } from "@/features/starters";
import { ActivityGroup, useMyItActivity } from "@/features/tickets";
import { useT } from "@/i18n";
import { useKiosk } from "@/lib/api";

import { SupportSection } from "./support-section";

/** Enough to recognise something without turning the rail into a second list page. */
const RAIL_LIMIT = 4;

export interface MySupportRailProps {
  className?: string;
}

export function MySupportRail({ className }: MySupportRailProps) {
  const { t } = useT("assistant");
  const { t: tc } = useT();
  const {
    waiting,
    inProgress,
    resolvedItems,
    accessWaiting,
    accessInProgress,
    accessFinished,
    open,
    requesterUnresolved,
  } = useMyItActivity();
  // 6 Oct: access cases the reader is involved in, as one card each.
  const waitingCount = waiting.length + accessWaiting.length;
  const recordsCount = inProgress.length + accessInProgress.length;
  const resolvedCount = resolvedItems.length + accessFinished.length;
  const conversations = useConversations({ limit: RAIL_LIMIT });
  const chats = conversations.data?.items ?? [];
  const kiosk = useKiosk();

  // On a shared device "my" tickets and chats would be the device's, i.e.
  // everyone's; the server returns none, and the rail says nothing (1 Oct).
  if (kiosk !== null) {
    return null;
  }

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {/* The rail's heading is also the way to the full list. It sits above
          "Waiting on Me" as a section header rather than in the page chrome,
          because this is where someone is already looking when they want more
          than four rows of it.

          The app header keeps its own copy for the cases this cannot cover —
          see `AppHeader`: no activity means no rail, and the rail is hidden
          below `lg`. Exactly one of the two is ever on screen. */}
      <Link
        to="/tickets"
        className="group hover:text-accent-fg-subtle ease-standard flex min-w-0 items-center gap-2 rounded-md transition-colors"
      >
        <Icon icon={Ticket} size="sm" tone="accent" />
        <Typography as="h2" variant="heading-6" className="min-w-0 flex-1 text-balance">
          {tc("nav.tickets")}
        </Typography>
        <Icon
          icon={ChevronRight}
          size="sm"
          className="text-fg-subtle ease-standard shrink-0 transition-transform group-hover:translate-x-0.5"
        />
      </Link>

      {/* An errored read renders the same notice through `ErrorState`. */}
      {requesterUnresolved && !open.isError ? <RequesterUnresolvedNotice /> : null}

      {open.isError ? (
        <ErrorState variant="inline" error={open.error} onRetry={() => void open.refetch()} />
      ) : (
        <>
          {/* "Waiting on Me" earns a heading and a card only when something is
              actually waiting. Nothing needing attention is the normal state,
              and a full section announcing it took the most prominent slot in
              the rail to say nothing — so it collapses to one quiet line and
              gives the space back to the groups that have content. */}
          {open.isPending ? (
            <LoadingRows rows={2} />
          ) : waitingCount === 0 ? (
            <Typography as="p" variant="caption" tone="muted" className="flex items-center gap-1.5">
              <Icon icon={CircleCheck} size="xs" tone="success" />
              {t("support.nothingWaiting")}
            </Typography>
          ) : (
            <SupportSection
              defaultOpen
              collapsible={false}
              label={t("support.waiting")}
              count={waitingCount}
            >
              <ActivityGroup
                label={t("support.waiting")}
                tickets={waiting}
                accessCases={accessWaiting}
                needsYou
                hideLabel
              />
            </SupportSection>
          )}

          {/* A new starter case waiting on this person (2 Oct); nothing otherwise. */}
          <StarterActionLink />

          <SupportSection defaultOpen={false} label={t("support.records")} count={recordsCount}>
            {recordsCount === 0 ? (
              <Typography as="p" variant="caption" tone="muted">
                {t("support.empty")}
              </Typography>
            ) : (
              <ActivityGroup
                label={t("support.records")}
                accessCases={accessInProgress.slice(0, RAIL_LIMIT)}
                tickets={inProgress.slice(0, Math.max(0, RAIL_LIMIT - accessInProgress.length))}
                hideLabel
              />
            )}
          </SupportSection>

          <SupportSection defaultOpen={false} label={t("support.recentChats")} count={chats.length}>
            {chats.length === 0 ? (
              <Typography as="p" variant="caption" tone="muted">
                {t("support.empty")}
              </Typography>
            ) : (
              <ul className="flex list-none flex-col gap-2 p-0">
                {chats.map((conversation) => (
                  <li key={conversation.conversation_id}>
                    <Card padding="sm" className="ease-standard transition hover:shadow-md">
                      <Link
                        to={`/ask?c=${conversation.conversation_id}`}
                        className="flex flex-col gap-1 rounded-md"
                      >
                        <Typography
                          as="span"
                          variant="caption"
                          className="text-fg line-clamp-2 font-medium"
                        >
                          <TranslatedText value={conversation.title} />
                        </Typography>
                        <Typography as="span" variant="caption" tone="muted">
                          <RelativeTime value={conversation.updated_at} />
                        </Typography>
                      </Link>
                    </Card>
                  </li>
                ))}
              </ul>
            )}
          </SupportSection>

          <SupportSection defaultOpen={false} label={t("support.resolved")} count={resolvedCount}>
            {resolvedCount === 0 ? (
              <Typography as="p" variant="caption" tone="muted">
                {t("support.empty")}
              </Typography>
            ) : (
              <ActivityGroup
                label={t("support.resolved")}
                accessCases={accessFinished.slice(0, RAIL_LIMIT)}
                tickets={resolvedItems.slice(0, Math.max(0, RAIL_LIMIT - accessFinished.length))}
                hideLabel
              />
            )}
          </SupportSection>
        </>
      )}
    </div>
  );
}
